import Stepper from "@/components/common/Stepper";
import AdminCreateUserForm from "@/features/admin/components/AdminCreateUserForm";
import { AdminCreateUserWizardNav } from "@/features/admin/components/AdminCreateUserWizardNav";
import { getCreateUserWizardSteps, validateCreateUserWizardSearch } from "@/features/admin/utils/createUserWizard";
import { useProfileByIdQuerySafe } from "@/features/profile/api/selectProfile";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ui } from "@patient-watch/ui";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/_app/users/create-user/details")({
    validateSearch: validateCreateUserWizardSearch,
    component: CreateUserDetailsStep,
});

function CreateUserDetailsStep() {
    const search = Route.useSearch();
    const navigate = useNavigate();

    if (search.userId) {
        return <CreateUserDetailsResume userId={search.userId} search={search} />;
    }

    return (
        <div className="space-y-6" data-testid="create-user-details-step">
            <Stepper steps={getCreateUserWizardSteps("details", search)} />
            <div className="space-y-2">
                <h2 className="font-medium text-lg">User details</h2>
                <p className="text-muted-foreground text-sm">
                    Create the account with name and email. Organisation, access, templates, and billing come next.
                </p>
            </div>
            <AdminCreateUserForm
                onCreated={(userId) => {
                    void navigate({
                        to: "/users/create-user/organisations",
                        search: { userId },
                    });
                }}
            />
        </div>
    );
}

function CreateUserDetailsResume({ userId, search }: { userId: string; search: { userId?: string } }) {
    const { data: profile, isLoading } = useProfileByIdQuerySafe(userId);
    const displayName =
        [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || profile?.email || "this user";

    return (
        <div className="space-y-6" data-testid="create-user-details-resume">
            <Stepper steps={getCreateUserWizardSteps("details", search)} />
            <div className="space-y-2">
                <h2 className="font-medium text-lg">User already created</h2>
                <p className="text-muted-foreground text-sm">
                    Continue with organisation, access, templates, and billing for this account. Creating again is
                    blocked so you do not duplicate the user.
                </p>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-10">
                    <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                </div>
            ) : (
                <ui.Alert data-testid="create-user-existing-summary">
                    <ui.AlertTitle>{displayName}</ui.AlertTitle>
                    <ui.AlertDescription>{profile?.email ?? userId}</ui.AlertDescription>
                </ui.Alert>
            )}

            <AdminCreateUserWizardNav
                search={search}
                nextTo="/users/create-user/organisations"
                nextLabel="Continue"
                nextDisabled={isLoading || !profile}
            />
        </div>
    );
}
